import { observer } from "mobx-react-lite"
import React, { FC, useCallback, useEffect } from "react"
import {
  Appearance,
  ImageStyle,
  Linking,
  Platform,
  StyleProp,
  TextStyle,
  TouchableOpacity,
  ViewStyle,
} from "react-native"
import { AutoImage, Button, Intercom, JalaIcon, LazyRender, Screen, Text } from "../../../components"
// import { useStores } from "../../models"
import { presets } from "../../../components/text/text.presets"
import { TxKeyPath } from "../../../i18n"
import { useStores } from "../../../models"
import { LoginStack, navigate, resetRoot } from "../../../navigators"
import { color, nativeBaseTheme, images } from "../../../theme"
import config from "../../../utils/config"
import InAppReview from 'react-native-in-app-review';
import { Skeleton, Modal, useDisclose, View } from "native-base"
import codePushCheck from "../../../utils/code-push"
import { useFocusEffect } from "@react-navigation/native"
import JalaAnalytic from "../../../utils/jala-analytic"

const ROOT: ViewStyle = {
  flexGrow: 1,
}

const PROFILE: ViewStyle = {
  backgroundColor: color.palette.white,
  alignItems: "center",
}

const IMG_PROFILE: ImageStyle = {
  width: 68,
  height: 68,
  borderRadius: 68 / 2,
  borderColor: color.palette.gray200,
  borderWidth: 3.09,
  marginTop: 11,
  marginBottom: 16,
}

const EMAIL_TEXT: TextStyle = {
  marginVertical: 4,
}

const VERIFY_WRAPPER: ViewStyle = {
  backgroundColor: color.palette.blueJalaLight,
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  paddingLeft: 8,
  paddingRight: 10,
  paddingVertical: 6,
  borderRadius: 24,
  marginVertical: 16,
}

const IMG_VERIFIED: ImageStyle = {
  width: 18,
  height: 18,
  borderRadius: 18 / 2,
  marginRight: 4,
  backgroundColor: color.primary,
}

const BUTTON_EDIT: ViewStyle = {
  paddingVertical: -4,
  paddingHorizontal: -4,
  backgroundColor: color.palette.white,
}

const BUTTON_EDIT_TEXT: TextStyle = {
  ...presets().title3,
  color: color.primary,
}

const FARM_BUTTON: ViewStyle = {
  marginHorizontal: 17,
  marginVertical: 16,
  paddingHorizontal: 16,
  paddingVertical: 15,
  flexDirection: "row",
  borderWidth: 1,
  borderColor: color.palette.gray200,
  borderRadius: 8,
  alignSelf: "stretch",
  alignItems: "center",
  justifyContent: "flex-start",
  backgroundColor: color.palette.white,
}

const SHRIMP_WRAPPER: ViewStyle = {
  width: 24,
  height: 24,
  borderRadius: 2.5,
  backgroundColor: color.primary,
}

const TEXT_BUTTON: TextStyle = {
  flex: 1,
  marginLeft: 14,
}

const BUTTON: ViewStyle = {
  marginHorizontal: 18,
  paddingVertical: 15,
  flexDirection: "row",
  backgroundColor: color.palette.white,
  borderRadius: 0,
  alignItems: 'center'
  // borderBottomWidth: 1,
  // borderColor: color.palette.gray200,
}

const DIVIDER: ViewStyle = {
  borderBottomWidth: 1,
  borderColor: color.palette.gray200,
}

const PADD: ViewStyle = {
  paddingHorizontal: 16,
  // marginBottom: 8
}

const HELP: ViewStyle = {
  paddingTop: 8,
  backgroundColor: color.palette.white,
}

const OTHER: ViewStyle = {
  paddingTop: 8,
  backgroundColor: color.palette.white,
}

const BOTTOM: ViewStyle = {
  backgroundColor: color.palette.white,
  paddingTop: 4,
  paddingBottom: 58.5,
}

const VERSION_TEXT: TextStyle = {
  color: color.palette.gray800,
  textAlign: "center",
  paddingVertical: 8,
}

const BAR: ViewStyle = {
  height: 4,
  backgroundColor: color.palette.background,
}

const BUTTON_LOGOUT_CONTAINER: ViewStyle = {
  flexDirection: 'row',
  justifyContent: 'space-between',
}

const BUTTON_LOGOUT_MODAL: ViewStyle = {
  width: '48%',
  borderRadius: 5,
  paddingVertical: 10,
  alignSelf: 'flex-end'
}

const BUTTON_BACK_MODAL: ViewStyle = {
  ...BUTTON_LOGOUT_MODAL,
  paddingVertical: 9,
}

const IMAGE: ImageStyle = {
  resizeMode: 'contain',
  width: "100%",
  height: 230,
  marginVertical: 16
}

interface customButtonProps {
  text?: TxKeyPath,

  onPress?(): void,

  right?: boolean,
  style?: StyleProp<ViewStyle>,
  icon?: string
  colorScheme?: any
}

const CustomButton = (props: customButtonProps) => {
  const { text, onPress, style, right = true, icon, colorScheme } = props
  const isShrimp = icon === "shrimp";
  const colour = isShrimp ? color.palette.white : color.palette.gray800;
  const styles = [BUTTON, style]
  return (
    <Button testID="jala:id/custom-button" preset="link" style={styles} onPress={onPress} colorScheme={colorScheme}>
      {icon && <View style={isShrimp && SHRIMP_WRAPPER}><JalaIcon name={icon} size={24}
        color={colour} /></View>}
      <Text preset={isShrimp ? "title3" : "body2"} tx={text} style={TEXT_BUTTON} colorScheme={colorScheme} />
      {right && <JalaIcon name="chevron_right" size={24} color={color.palette.gray800} />}
    </Button>
  )
}

const LogoutModal: FC<{ colorScheme?: "light" | "dark", onLogout?: () => void }> = observer(function LogoutModal(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), children, onLogout } = props;
  const { isOpen, onClose, onOpen } = useDisclose()

  return (<>
    <TouchableOpacity onPress={onOpen}>
      {children}
    </TouchableOpacity>
    <Modal isOpen={isOpen} onClose={onClose}>
      <View
        alignSelf="stretch"
        marginY="80px"
        marginX="16px"
        padding="16px"
        rounded="8px"
        backgroundColor={colorScheme === 'light' ? nativeBaseTheme.colors.light["50"] : nativeBaseTheme.colors.dark["50"]}
      >
        <View marginBottom="16px">
          <Text preset="display2" color={color.palette.gray900} tx="profileScreen.logoutTitle" textAlign="center" />
          <AutoImage source={images.logout} style={IMAGE} />
          <Text preset="body1" color={color.palette.gray800} tx="profileScreen.logoutSubtitle" textAlign="center" />
        </View>
        <View style={BUTTON_LOGOUT_CONTAINER}>
          <Button
            testID="jala:id/cancel-button"
            tx="profileScreen.logoutButtonCancel"
            preset="outline"
            colorScheme={colorScheme}
            style={BUTTON_BACK_MODAL}
            onPress={onClose}
          />
          <Button
            testID="jala:id/confirm-button"
            tx="profileScreen.logoutButtonConfirm"
            preset="primary"
            colorScheme={colorScheme}
            style={BUTTON_LOGOUT_MODAL}
            onPress={onLogout}
          />
        </View>
      </View>
    </Modal>
  </>)
})


export const ProfileScreen: FC<{ colorScheme: "light" | "dark" }> = observer(function ProfileScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme()} = props

  // Pull in one of our MST stores
  const rootStore = useStores();
  const { appStore: { appVersion }, tokenStore, userStore: { user, loading } } = rootStore
  const { token } = tokenStore

  const { BASE_URL } = config();

  useEffect(() => {
    if(!token) resetRoot(LoginStack)
  }, [token])

  useFocusEffect(useCallback(() => {
    JalaAnalytic.log('Mobile app: navigation - account')
  }, []));

  const onPressLink = (forceLink = false) => {
    // Give you result if version of device supported to rate app or not!
    if (!InAppReview.isAvailable() || forceLink) {
      const link = Platform.OS === 'ios'
        ? 'https://apps.apple.com/us/app/jala-tech/id1582412818'
        : 'https://play.google.com/store/apps/details?id=tech.jala.android';
      return Linking.openURL(link).catch();
    }
    // trigger UI InAppreview
    InAppReview.isAvailable();
    return InAppReview.RequestInAppReview()
      .then((hasFlowFinishedSuccessfully) => {
        if (hasFlowFinishedSuccessfully) {
          onPressLink(true);
        }
        // if (!hasFlowFinishedSuccessfully && Platform.OS === 'ios') {
        //   onPressLink(true);
        // }
      })
      .catch();
  }

  const onPressButton = (key: string) => () => {
    switch (key) {
      case "flag":
        navigate('App.Stack', 'Profile.Stack', { screen: "Profile.Change.Language" });
        break
      case "help":
        // navigate('App.Stack, 'Help');
        break
      case "policy":
        navigate('App.Stack', 'Profile.Stack', { screen: "Profile.App.Policy" });
        break
      case "rate":
        onPressLink();
        break
      case "personalisation":
        navigate('App.Stack', 'Profile.Stack', { screen: "Profile.Change.Interest" });
        break
      case "faq":
        Linking.openURL("https://www.notion.so/Jala-Help-Center-86d9d38190214ed08e5d7c0aa436cf17").catch()
        break
      case "whatsapp":
        Linking.openURL("https://wa.me/6281325514194").catch()
        break
      case "messenger":
        Linking.openURL("https://m.me/jala.Technology/").catch()
        break
      case "email":
        Linking.openURL("mailto:support@jala.tech").catch()
        break
      case "tutorial":
        Linking.openURL("https://www.youtube.com/watch?v=MCm-MEf9_QQ&list=PLDF2porrO_jy839wyxr8mL6WTQK31Fetw").catch()
        break
      case "telegram":
        Linking.openURL("https://t.me/joinchat/OCBsJShz0WEzMmFl").catch()
        break
      case "info":
        navigate('App.Stack', 'Profile.Stack', { screen: "Profile.App.Info" })
        break
      case "settings":
        navigate('App.Stack', 'Profile.Stack', { screen: "Profile.Change.Setting.Account" });
        break
      case "logout":
        if (Platform.OS !== 'web') Intercom.logout();
        JalaAnalytic.logout();
        rootStore.reset()
        break
      default:
    }
  }

  const onNavigate = useCallback((key: string) => () => {
    if (key === "profile") {
      JalaAnalytic.log('Mobile app: account - edit');
      navigate('App.Stack', 'Profile.Stack', { screen: "Profile.Change.Edit.Profile" })
    }
    else navigate('App.Stack', 'Profile.Stack', { screen: "Profile.Connected.Farm" })
  }, [])

  const Profile = useCallback(() => {
    const { avatar, name, email, phone } = (user || {});
    const url = `${BASE_URL}/img/cache/medium/`;
    const img = !user ? {uri: `${url}/users/default.png`} : { uri: `${url}${avatar}` };
    const verified = require("./verified.png");
    return (
      <View style={PROFILE}>
        <AutoImage source={img} style={IMG_PROFILE} />
        <Text preset="title3" textAlign="center" mx="16px" isTruncated numberOfLines={2} flex={1} text={name} colorScheme={colorScheme} />
        <Text preset="body1" text={email} style={EMAIL_TEXT} colorScheme={colorScheme} />
        <Text preset="body2" text={phone} colorScheme={colorScheme} />
        <View style={VERIFY_WRAPPER}>
          <AutoImage source={verified} style={IMG_VERIFIED} />
          <Text preset="body2" tx="profileScreen.verified" color={color.palette.gray800} colorScheme={colorScheme} />
        </View>
        <Button testID="jala:id/edit-button" tx="profileScreen.editProfile" onPress={onNavigate("profile")} style={BUTTON_EDIT} textStyle={BUTTON_EDIT_TEXT} colorScheme={colorScheme} />
        <CustomButton text="profileScreen.farmProfile" icon="shrimp" style={FARM_BUTTON} onPress={onNavigate("farm")} colorScheme={colorScheme} />
      </View>
    )
  }, [BASE_URL, colorScheme, onNavigate, user])

  const renderProfilePlaceholder = () => {
    return (
      <View style={PROFILE}>
        <Skeleton height="68px" width="68px" borderRadius="34px" marginBottom="16px" marginTop="11px" />
        <Skeleton.Text lines={1} height="24px" width="50%" />
        <Skeleton.Text lines={1} height="20px" width="50%" />
        <Skeleton.Text lines={1} height="20px" width="50%" />
        <Skeleton height="28px" width="20%" borderRadius="24px" marginBottom="6px" marginTop="6px" marginRight="8px" marginLeft="8px" />
        <Skeleton height="24px" width="20%" borderRadius="2px" marginBottom="6px" marginTop="6px" marginRight="8px" marginLeft="8px" />
      </View>
    )
  }


  return (
    <Screen
      style={ROOT}
      preset="scroll"
      colorScheme={colorScheme}
      backgroundBar={colorScheme === "light" ? color.primary : undefined}
      statusBar={colorScheme === "light" ? "light-content" : "dark-content"}
      hideOnBlurred={false}
    >
      <LazyRender loading={loading} renderPlaceholder={renderProfilePlaceholder}>
        {user && <Profile /> }
      </LazyRender>
      <View style={BAR} />
      <View mt="16px">
        <Text preset="title3" tx="profileScreen.security" style={PADD} colorScheme={colorScheme} />
        <CustomButton text="profileScreen.settingAccount" icon="setting" style={DIVIDER} onPress={onPressButton("settings")} colorScheme={colorScheme} />
        <CustomButton text="profileScreen.privacyMenu" icon="password" colorScheme={colorScheme} onPress={onPressButton("policy")} />
        <View style={BAR} />
        <Text preset="title3" tx="profileScreen.choice" style={PADD} mt="20px" colorScheme={colorScheme} />
        <CustomButton text="profileScreen.interesTitle" icon="display" style={DIVIDER} colorScheme={colorScheme} onPress={onPressButton("personalisation")} />
        <CustomButton text="profileScreen.language" icon="language" colorScheme={colorScheme} onPress={onPressButton("flag")}/>
      </View>
      <View style={BAR} />
      <View style={HELP}>
        <Text preset="title3" tx="profileScreen.support" style={PADD} colorScheme={colorScheme} />
        <CustomButton text="profileScreen.faq" icon="help" style={DIVIDER} onPress={onPressButton("faq")} colorScheme={colorScheme} />
        <CustomButton text="profileScreen.whatsapp" icon="whatsapp" style={DIVIDER} onPress={onPressButton("whatsapp")} colorScheme={colorScheme} />
        <CustomButton text="profileScreen.messenger" icon="messenger" style={DIVIDER} onPress={onPressButton("messenger")} colorScheme={colorScheme} />
        <CustomButton text="profileScreen.emailMenu" icon="email" style={DIVIDER} onPress={onPressButton("email")} colorScheme={colorScheme} />
        <CustomButton text="profileScreen.appsTutorial" icon="youtube" style={DIVIDER} onPress={onPressButton("tutorial")} colorScheme={colorScheme} />
        <CustomButton text="profileScreen.joinTelegram" icon="telegram" onPress={onPressButton("telegram")} colorScheme={colorScheme} />
      </View>
      <View style={BAR} />
      <View style={OTHER}>
        <Text preset="title3" tx="profileScreen.theOther" style={PADD} colorScheme={colorScheme} />
        <CustomButton text="profileScreen.rateUs" icon="favorite" style={DIVIDER} colorScheme={colorScheme} onPress={onPressButton("rate")} />
        <CustomButton text="profileScreen.appInfo" icon="info-fill" colorScheme={colorScheme} onPress={onPressButton("info")} />
      </View>
      <View style={BAR} />
      <View style={BOTTOM}>
        <LogoutModal onLogout={onPressButton("logout")}>
          {/* <View style={BUTTON}> */}
          <View style={[BUTTON, DIVIDER]} mb="6px">
            <JalaIcon name="logout" size={24}color={color.palette.red} />
            <Text preset="body2" tx="profileScreen.exitMenu" color={color.palette.red} style={TEXT_BUTTON} colorScheme={colorScheme} />
          </View>
        </LogoutModal>
        <TouchableOpacity onPress={() => codePushCheck(true)}>
          <Text preset="body3" text={`v${appVersion}`} style={VERSION_TEXT} colorScheme={colorScheme} />
        </TouchableOpacity>
      </View>
    </Screen>
  )
})
